
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Menu } from "lucide-react";
import Logo from "@/components/common/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { generateInitials } from "@/utils/userUtils";

const Header = () => {
  const { user, profile, signOut, isLoading } = useAuth();

  // Generate user initials from profile name
  const userInitials = generateInitials(profile?.full_name);

  const handleLogout = () => {
    signOut();
  };

  return (
    <header className="border-b border-border bg-godyspo-background pt-[env(safe-area-inset-top)]">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Logo size="medium" />
        <div className="flex items-center gap-4">
          {isLoading ? (
            // Show loading state while determining auth status
            <div className="flex items-center space-x-4">
              <div className="h-10 w-20 bg-gray-200 animate-pulse rounded"></div>
              <div className="h-10 w-24 bg-gray-200 animate-pulse rounded"></div>
            </div>
          ) : user && profile ? (
            // Show user info and dropdown when logged in
            <div className="flex items-center space-x-4">
              <Link to="/seller/dashboard">
                <Button className="bg-godyspo-gold text-godyspo-background hover:bg-godyspo-gold/90">
                  Dashboard
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      {profile?.profile_image_url && (
                        <AvatarImage 
                          src={profile.profile_image_url} 
                          alt={profile?.full_name || "User profile"} 
                        />
                      )}
                      <AvatarFallback className="bg-godyspo-blue text-white">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/profile">My Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            // Show navigation when not logged in
            <>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/buyer" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  For Buyers
                </Link>
                <Link to="/consumer" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  For Homeowners
                </Link>
                <Link to="/blog" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Blog
                </Link>
                <Link to="/seller/faq" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  FAQ
                </Link>
                <Link to="/contact" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Contact
                </Link>
                <Link to="/seller/login" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Sign In
                </Link>
              </nav>

              {/* Mobile Dropdown Menu */}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-godyspo-gold text-godyspo-background hover:bg-godyspo-gold/90">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    className="w-56 bg-background border-border z-50" 
                    align="end"
                  >
                    <DropdownMenuItem asChild>
                      <Link to="/seller/register" className="text-[#ffd700] hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        Start Free Trial
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/seller/login" className="cursor-pointer">
                        Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/buyer" className="cursor-pointer">
                        For Buyers
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/consumer" className="cursor-pointer">
                        For Homeowners
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/blog" className="cursor-pointer">
                        Blog
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/seller/faq" className="cursor-pointer">
                        FAQ
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/contact" className="cursor-pointer">
                        Contact
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Desktop CTA Button */}
              <Link to="/seller/register" className="hidden md:block">
                <Button className="bg-godyspo-gold text-godyspo-background hover:bg-godyspo-gold/90">
                  Start Free Trial
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
